import { useState, useEffect } from 'react';
import api from '../../hooks/useApi';
import { formatMoney, formatDate } from '../../utils/format';

const PAID_STATUSES = new Set(['paid', 'Paid', 'PAID']);

function groupByMonth(items) {
  const map = {};
  for (const item of items) {
    const key = item.est_pay_date ? item.est_pay_date.slice(0, 7) : 'Unscheduled';
    if (!map[key]) map[key] = [];
    map[key].push(item);
  }
  return map;
}

function monthLabel(key) {
  if (key === 'Unscheduled') return 'Unscheduled';
  const [y, m] = key.split('-');
  return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function isPaid(status) { return PAID_STATUSES.has(status); }
function isOverdue(payDate) { return !!payDate && payDate < new Date().toISOString().split('T')[0]; }

function compact(n) {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `£${(n / 1_000).toFixed(0)}k`;
  return `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toISO(d) { return d.toISOString().split('T')[0]; }

// 8 Monday-anchored weekly buckets from today's week onward
function buildWeekBuckets(today) {
  const base = new Date(today + 'T00:00:00');
  const dow = base.getDay(); // 0=Sun
  base.setDate(base.getDate() + (dow === 0 ? -6 : 1 - dow)); // rewind to Monday

  return Array.from({ length: 8 }, (_, i) => {
    const start = new Date(base);
    start.setDate(base.getDate() + i * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const startISO = toISO(start);
    const endISO   = toISO(end);
    return {
      start:     startISO,
      end:       endISO,
      isCurrent: i === 0,
      isPast:    endISO < today,
      shortLabel: start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      rangeLabel: `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`,
      items: [],
      total: 0,
    };
  });
}

export default function ExpectedIncomePage() {
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [hidePaid, setHidePaid]         = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(null);

  useEffect(() => {
    api.get('expected-income')
      .then(d => { setItems(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().split('T')[0];

  const overdue          = items.filter(i => i.est_pay_date && i.est_pay_date < today && !isPaid(i.status));
  const totalOutstanding = items.filter(i => !isPaid(i.status)).reduce((s, i) => s + (i.invoice_amount_excl || 0), 0);
  const totalPaid        = items.filter(i =>  isPaid(i.status)).reduce((s, i) => s + (i.invoice_amount_excl || 0), 0);

  // Weekly buckets (unpaid only)
  const weekBuckets = buildWeekBuckets(today);
  for (const b of weekBuckets) {
    b.items = items.filter(i =>
      i.est_pay_date && i.est_pay_date >= b.start && i.est_pay_date <= b.end && !isPaid(i.status)
    );
    b.total = b.items.reduce((s, i) => s + (i.invoice_amount_excl || 0), 0);
  }
  const weekMax      = Math.max(...weekBuckets.map(b => b.total), 1);
  const weekTotal8   = weekBuckets.reduce((s, b) => s + b.total, 0);

  // Running cumulative for the chart
  let running = 0;
  const cumulatives = weekBuckets.map(b => { running += b.total; return running; });

  // Month-grouped table
  const visibleItems = hidePaid ? items.filter(i => !isPaid(i.status)) : items;
  const grouped = groupByMonth(visibleItems);
  const months  = Object.keys(grouped).sort((a, b) =>
    a === 'Unscheduled' ? 1 : b === 'Unscheduled' ? -1 : a.localeCompare(b)
  );

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Loading…</div>;

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Expected Income</h2>
          <div className="text-[9px] text-gray-500">Expected payment dates from invoiced milestones</div>
        </div>
        <button
          onClick={() => setHidePaid(h => !h)}
          className={`text-[9px] font-semibold px-2.5 py-1 rounded border transition-colors ${hidePaid ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-300 hover:border-navy'}`}
        >
          {hidePaid ? 'Show Paid' : 'Hide Paid'}
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        <div className="bg-white border border-gray-200 rounded p-2 min-w-0">
          <div className="text-[8px] text-gray-500 uppercase font-bold">Total Outstanding</div>
          <div className="text-base sm:text-lg font-extrabold text-blue-600 leading-tight tabular-nums truncate" title={formatMoney(totalOutstanding)}>{formatMoney(totalOutstanding)}</div>
          <div className="text-[8px] text-gray-400">{items.filter(i => !isPaid(i.status)).length} invoices</div>
        </div>
        <div className={`bg-white border rounded p-2 min-w-0 ${overdue.length ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
          <div className={`text-[8px] uppercase font-bold ${overdue.length ? 'text-red-600' : 'text-gray-500'}`}>Overdue</div>
          <div className={`text-base sm:text-lg font-extrabold leading-tight tabular-nums truncate ${overdue.length ? 'text-red-600' : 'text-navy'}`} title={formatMoney(overdue.reduce((s, i) => s + (i.invoice_amount_excl || 0), 0))}>
            {formatMoney(overdue.reduce((s, i) => s + (i.invoice_amount_excl || 0), 0))}
          </div>
          <div className={`text-[8px] ${overdue.length ? 'text-red-500' : 'text-gray-400'}`}>{overdue.length} overdue</div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-2 min-w-0">
          <div className="text-[8px] text-gray-500 uppercase font-bold">Next 8 Weeks</div>
          <div className="text-base sm:text-lg font-extrabold text-amber-500 leading-tight tabular-nums truncate" title={formatMoney(weekTotal8)}>{formatMoney(weekTotal8)}</div>
          <div className="text-[8px] text-gray-400">{weekBuckets.reduce((s, b) => s + b.items.length, 0)} invoices scheduled</div>
        </div>
      </div>

      {/* ── 8-Week cash flow bar chart ── */}
      <div className="bg-white border border-gray-200 rounded mb-4 overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-200 bg-navy/5 flex items-center justify-between">
          <div>
            <span className="text-[11px] font-bold text-navy">8-Week Income Forecast</span>
            <span className="ml-2 text-[8px] text-gray-400">unpaid invoices by expected payment week · click a bar to drill in</span>
          </div>
          <div className="flex items-center gap-3 text-[8px] text-gray-500">
            <span className="flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#1B2238' }} /> This week
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: '#D97706', opacity: 0.8 }} /> Upcoming
            </span>
          </div>
        </div>

        <div className="px-4 pt-4 pb-3">
          {/* Bar chart */}
          <div className="flex items-end gap-1" style={{ height: 136 }}>
            {weekBuckets.map((bucket, i) => {
              const BAR_AREA  = 100;
              const barH      = bucket.total > 0 ? Math.max(5, (bucket.total / weekMax) * BAR_AREA) : 0;
              const isEmpty   = bucket.total === 0;
              const isSelected = expandedWeek === i;
              const barColor  = bucket.isCurrent ? '#1B2238' : '#D97706';
              const barOpacity = isSelected ? 1 : (bucket.isCurrent ? 0.9 : Math.max(0.45, 0.8 - i * 0.04));

              return (
                <div
                  key={bucket.start}
                  className={`flex-1 flex flex-col items-center cursor-pointer group transition-all rounded-t ${isSelected ? 'bg-gold/5' : 'hover:bg-gray-50'}`}
                  onClick={() => setExpandedWeek(isSelected ? null : i)}
                  title={`${bucket.rangeLabel}: ${formatMoney(bucket.total)} · ${bucket.items.length} invoice${bucket.items.length !== 1 ? 's' : ''}`}
                >
                  {/* Amount label */}
                  <div className="text-[7px] font-bold text-center mb-0.5 leading-none" style={{ minHeight: 12, color: isEmpty ? '#CBD5E1' : barColor }}>
                    {!isEmpty && compact(bucket.total)}
                  </div>
                  {/* Bar */}
                  <div className="w-full flex items-end justify-center" style={{ height: BAR_AREA }}>
                    {isEmpty ? (
                      <div style={{ width: '55%', height: 3, background: '#E2E8F0', borderRadius: 2 }} />
                    ) : (
                      <div
                        style={{ width: '72%', height: barH, background: barColor, borderRadius: '3px 3px 0 0', opacity: barOpacity, transition: 'height 0.25s ease, opacity 0.2s' }}
                      />
                    )}
                  </div>
                  {/* Week label */}
                  <div className="mt-1 text-center leading-tight">
                    <div className={`text-[7px] font-semibold ${bucket.isCurrent ? 'text-navy' : 'text-gray-500'}`}>{bucket.shortLabel}</div>
                    {bucket.isCurrent && <div className="text-[6px] font-bold text-gold uppercase tracking-wide">this wk</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cumulative running total strip */}
          <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100">
            <div className="text-[7px] text-gray-400 font-semibold w-16 flex-shrink-0">Cumulative</div>
            {cumulatives.map((cum, i) => (
              <div key={i} className="flex-1 text-center">
                <span className={`text-[7px] font-semibold ${cum > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>
                  {cum > 0 ? compact(cum) : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Expanded week drill-in */}
        {expandedWeek !== null && (
          <div className="border-t border-gray-200 bg-gray-50 px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] font-bold text-navy">
                {weekBuckets[expandedWeek].rangeLabel}
                {weekBuckets[expandedWeek].isCurrent && <span className="ml-1.5 text-[7px] font-bold text-gold bg-gold/10 px-1.5 py-0.5 rounded">THIS WEEK</span>}
              </div>
              <div className="text-[9px] font-extrabold text-amber-600">{formatMoney(weekBuckets[expandedWeek].total)}</div>
            </div>
            {weekBuckets[expandedWeek].items.length === 0 ? (
              <div className="text-[9px] text-gray-400 text-center py-2">No invoices due this week.</div>
            ) : (
              <div className="space-y-1">
                {weekBuckets[expandedWeek].items.map(item => (
                  <div key={item.id} className="flex items-center justify-between bg-white border border-gray-200 rounded px-2.5 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[9px] font-bold text-navy flex-shrink-0">{item.milestone_code}</span>
                      <span className="text-[8px] text-gray-400 flex-shrink-0">·</span>
                      <span className="text-[9px] text-gray-700 truncate">{item.client_name || '—'}</span>
                      {item.sow_reference && <span className="text-[8px] text-gray-400 flex-shrink-0">({item.sow_reference})</span>}
                      {item.description && <span className="text-[8px] text-gray-400 truncate hidden sm:block">{item.description}</span>}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                      <span className="text-[8px] text-gray-400 font-mono">{item.invoice_number || '—'}</span>
                      <span className="text-[8px] text-gray-400">{formatDate(item.est_pay_date)}</span>
                      <span className="text-[9px] font-extrabold text-navy">{formatMoney(item.invoice_amount_excl)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Month-grouped detail table ── */}
      {items.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-[10px] text-gray-400">
          No invoiced milestones with expected payment dates yet. Record invoices from the Milestones page.
        </div>
      ) : (
        <div className="space-y-4">
          {months.map(monthKey => {
            const monthItems  = grouped[monthKey];
            const monthTotal  = monthItems.reduce((s, i) => s + (i.invoice_amount_excl || 0), 0);
            const isUnscheduled = monthKey === 'Unscheduled';
            const monthDate   = isUnscheduled ? null : monthKey;
            const isPastMonth = monthDate && monthDate < today.slice(0, 7) && monthItems.some(i => !isPaid(i.status));
            return (
              <div key={monthKey} className="bg-white border border-gray-200 rounded overflow-hidden">
                <div className={`px-3 py-2 border-b border-gray-200 flex items-center justify-between ${isPastMonth ? 'bg-red-50' : 'bg-navy/5'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-bold ${isPastMonth ? 'text-red-700' : 'text-navy'}`}>{monthLabel(monthKey)}</span>
                    {isPastMonth && <span className="text-[8px] font-bold text-red-500 bg-red-100 px-1.5 py-0.5 rounded">PAST DUE</span>}
                  </div>
                  <div className="text-right">
                    <div className="text-[8px] text-gray-500 uppercase font-bold">Month Total</div>
                    <div className={`text-[11px] font-extrabold ${isPastMonth ? 'text-red-600' : 'text-navy'}`}>{formatMoney(monthTotal)}</div>
                  </div>
                </div>
                <div className="overflow-x-auto"><table className="w-full">
                  <thead><tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">SOW</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Milestone</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Client</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Invoice #</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Invoice Date</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Pay Date</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Terms</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Amount (excl VAT)</th>
                    <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold">Amount (incl VAT)</th>
                    <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Status</th>
                  </tr></thead>
                  <tbody>
                    {monthItems.map(m => {
                      const overdue_ = isOverdue(m.est_pay_date) && !isPaid(m.status);
                      return (
                        <tr key={m.id} className={`border-b border-gray-100 hover:bg-gold/5 ${overdue_ ? 'bg-red-50/40' : ''}`}>
                          <td className="px-2 py-1 text-[9px] font-semibold">{m.sow_reference}</td>
                          <td className="px-2 py-1">
                            <div className="text-[10px] font-bold">{m.milestone_code}</div>
                            {m.description && <div className="text-[8px] text-gray-400 truncate max-w-[120px]">{m.description}</div>}
                          </td>
                          <td className="px-2 py-1 text-[9px] text-gray-600">{m.client_name || '—'}</td>
                          <td className="px-2 py-1 text-[9px] font-mono">{m.invoice_number || '—'}</td>
                          <td className="px-2 py-1 text-[9px]">{formatDate(m.formal_invoice_date)}</td>
                          <td className={`px-2 py-1 text-[9px] font-semibold ${overdue_ ? 'text-red-600' : ''}`}>
                            {formatDate(m.est_pay_date)}
                            {overdue_ && ' ⚠'}
                          </td>
                          <td className="px-2 py-1 text-[9px] text-gray-500">{m.payment_terms ? `${m.payment_terms}d` : '—'}</td>
                          <td className="px-2 py-1 text-[10px] font-semibold text-right text-navy">{formatMoney(m.invoice_amount_excl)}</td>
                          <td className="px-2 py-1 text-[10px] text-right text-gray-500">{formatMoney(m.invoice_amount_incl)}</td>
                          <td className="px-2 py-1">
                            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${isPaid(m.status) ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                              {m.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td colSpan="7" className="px-2 py-1 text-[9px] font-bold text-gray-500 text-right">Month Total</td>
                      <td className="px-2 py-1 text-[10px] font-extrabold text-right text-navy">{formatMoney(monthTotal)}</td>
                      <td colSpan="2" />
                    </tr>
                  </tfoot>
                </table></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
